import { useEffect, useState } from "react";
import { Button, Form, Input, Space, message, Checkbox } from "antd";
import { Show } from "@refinedev/antd";
import { AppImageUploader, AppTitle, EFileTypes } from "../../../components";
import axAPI from "../../../api/axios-instance";
import { v4 as randomId } from "uuid";

type BannerFormValues = {
  imgUrl?: string;
  docUrl?: string;
  isActive?: boolean;
};

type BannerResponse = BannerFormValues & { id: number };

export const BannersPage = () => {
  const [form] = Form.useForm<BannerFormValues>();
  const [banner, setBanner] = useState<BannerResponse | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const loadBanner = async () => {
    setLoading(true);
    try {
      const { data } = await axAPI.get("/cms/league/documents/banners");
      const latestBanner: BannerResponse | undefined =
        Array.isArray(data) && data.length ? data[data.length - 1] : undefined;

      setBanner(latestBanner);
      if (latestBanner) {
        form.setFieldsValue({
          imgUrl: latestBanner.imgUrl,
          docUrl: latestBanner.docUrl,
          isActive: !!latestBanner.isActive,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ isActive: false });
      }
    } catch (error: any) {
      message.error(error?.message || "Не удалось загрузить баннер");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanner();
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        isActive: !!values.isActive,
      };
      if (banner?.id) {
        await axAPI.put("/cms/league/documents/banner", {
          id: banner.id,
          ...payload,
        });
        message.success("Баннер обновлен");
        await loadBanner();
      } else {
        const { data } = await axAPI.post(
          "/cms/league/documents/banner",
          values
        );
        message.success("Баннер создан");
        setBanner({ id: data, ...payload });
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error(error?.message || "Не удалось сохранить баннер");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Show
      isLoading={loading}
      title={<AppTitle>Баннеры</AppTitle>}
      breadcrumb={false}
      headerButtons={<></>}
      footerButtons={null}
      goBack={false}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name={"imgUrl"}
          label={"Изображение баннера"}
          rules={[
            {
              required: true,
              message: "Загрузите изображение баннера",
            },
          ]}
        >
          <AppImageUploader
            type="DOCUMENTS"
            entityId={`${randomId()}`}
            serviceType={EFileTypes.DOCUMENTS}
            cropperProps={{
              modalTitle: "Редактировать изображение баннера",
            }}
            uploadProps={{
              multiple: false,
              children: "Изображение баннера",
              listType: "picture-card",
            }}
          />
        </Form.Item>

        <Form.Item
          name={"docUrl"}
          label={"Ссылка на документ"}
          rules={[
            {
              required: true,
              message: "Укажите ссылку на документ",
            },
            {
              type: "url",
              message: "Введите корректную ссылку (http/https)",
            },
          ]}
        >
          <Input placeholder={"Вставьте ссылку на документ"} />
        </Form.Item>
        <Form.Item
          name="isActive"
          valuePropName="checked"
          style={{ marginBottom: 16 }}
        >
          <Checkbox>Активность</Checkbox>
        </Form.Item>
        <Space>
          <Button
            type={"primary"}
            onClick={handleSubmit}
            loading={saving}
            disabled={loading}
          >
            Сохранить
          </Button>
          <Button onClick={() => form.resetFields()} disabled={saving}>
            Очистить
          </Button>
        </Space>
      </Form>
    </Show>
  );
};
